<template>
  <b-modal
    v-model="modalShow"
    size="xl"
    title="Scanned Purchase Order Details"
    class="purchase-order-modal"
    content-class="p-0"
    no-close-on-backdrop
    hide-footer
    @hidden="$emit('modal-closed')"
  >
    <div
      v-if="loading"
      class="text-center p-3"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
      class="m-3"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <div v-if="!loading && !loadingError">

      <purchase-order-info
        v-if="!loading && !loadingError"
        :item="purchaseOrderDetail"
      />

      <div
        class="d-flex align-items-center my-1"
        style="width: 100%; gap: 20px;"
      >
        <div>Matched with PO Items : {{ purchaseOrderDetail.matched_items }} / {{ purchaseOrderDetail.items.length }}</div>

        <div v-if="purchaseOrderDetail.matched_items == 0">
          <span
            v-if="purchaseOrderDetail.no_match_aknowleged"
            class="text-primary"
          >
            No Match Aknowleged
          </span>
          <aknowledge-no-items-match
            v-else
            :received-po-id="purchaseOrderDetail.id"
            @aknowleged="aknowledgeHandler"
          />
        </div>

        <initiate-p-o-matching :invoice-id="invoiceId" />
      </div>

      <div class="table-wrapper">
        <b-table
          responsive
          :items="sortedItems"
          :fields="tableFields"
          :tbody-tr-class="rowClass"
          hover
          class="compact-table border-table"
          small
        >
          <template #head(box_qty)="">
            <div class="d-flex flex-column align-items-start justify-content-between">
              <span>Box Quantity</span>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="px-1"
                @click="fillBoxQtyFromMfc"
              >
                Fill Default
              </b-button>
            </div>
          </template>

          <!-- Custom header for New Lot -->
          <template
            #head(lot)=""
          >
            <div class="d-flex">
              <div class="d-flex flex-column align-items-start justify-content-between">
                <span>New Lot  <feather-icon
                  icon="RepeatIcon"
                  size="20"
                  class="cursor-pointer ml-1 rounded text-primary"
                  style="border: 1px solid green;padding: 2px;margin: 2px 0;"
                  @click="resetLot"
                /></span>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="px-1"
                  @click="fillLotFromOldLot"
                >
                  Fill Old Lot
                </b-button>
              </div>

            </div>
          </template>
          <template #cell(box_qty)="data">
            <b-form-input
              v-model="data.item.box_qty"
              class="py-0"
              style="max-width: 100px;"
              :class="{'is-invalid': validateMode && data.item.boxQtyInvalid}"
              @input="updateItemColor(data.index)"
            />
            <small
              v-if="validateMode && data.item.boxQtyInvalid"
              class="text-danger"
            >
              Numbers only
            </small>
          </template>
          <template #cell(old_lot)="data">
            <div class="d-flex align-items-center justify-content-between">
              <span>{{ data.value }}</span>
              <feather-icon
                v-if="data.value && !areLotValuesEqual(data.item.lot, data.item.old_lot)"
                icon="FastForwardIcon"
                size="15"
                class="cursor-pointer text-primary"
                @click="fillLotFromOldLot(data.index)"
              />
            </div>
          </template>
          <template #cell(lot)="data">
            <div class="position-relative">
              <b-form-input
                v-model="data.item.lot"
                class="py-0"
                style="max-width: 100px;"
                :class="{'is-invalid': validateMode && (data.item.lotInvalid || data.item.lot === 'calc-error')}"
                @input="updateItemColor(data.index)"
              />
              <feather-icon
                v-if="data.item.lot"
                icon="RepeatIcon"
                size="15"
                class="cursor-pointer text-primary position-absolute"
                style="left: 6em; top: 50%; transform: translateY(-50%);"
                @click="resetSingleLot(data.index)"
              />
            </div>
            <small
              v-if="validateMode && data.item.lotInvalid"
              class="text-danger"
            >
              Numbers, +, spaces only
            </small>
          </template>
        </b-table>
      </div>

      <!-- Custom footer -->
      <div
        class="modal-footer border-top"
      >
        <div
          v-if="validateMode && hasInvalidInputs"
          class="text-danger me-auto"
        >
          Please correct highlighted fields
        </div>
        <b-button
          v-if="purchaseOrderDetail.status === 'pending'"
          variant="success"
          class="ms-auto"
          :disabled="saving"
          @click="validateAndSave('approve')"
        >
          Approve {{ saving ? '' : '' }} <b-spinner
            v-if="saving"
            small
          />
        </b-button>
        <b-button
          v-else
          variant="primary"
          :disabled="saving"
          @click="validateAndSave('update')"
        >
          Update {{ saving ? '' : '' }} <b-spinner
            v-if="saving"
            small
          />
        </b-button>
        <b-button
          variant="secondary"
          @click="modalShow=false"
        >
          Close
        </b-button>
      </div>

    </div>

  </b-modal>
</template>

<script>
import {
  BButton, BAlert, BSpinner,
  BTable, BFormInput,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import InitiatePOMatching from '@/components/PoInvoiceMatch/InitiatePOMatching.vue'
import PurchaseOrderInfo from './PurchaseOrderInfo.vue'
import AknowledgeNoItemsMatch from './UnmatchedInvoice/AknowledgeNoItemsMatch.vue'

export default {
  components: {
    BButton,
    BAlert,
    BSpinner,
    BTable,
    BFormInput,
    PurchaseOrderInfo,
    InitiatePOMatching,
    AknowledgeNoItemsMatch,
  },
  props: {
    invoiceId: {
      type: Number,
      required: true,
    },
    refresh: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      purchaseOrderDetail: null,
      modalShow: true,
      loadingError: null,
      loading: true,
      saving: false,
      validateMode: false, // Only show validation errors after first submit attempt
      debugMode: true, // Set to false in production
      tableFields: [
        { key: 'id', label: 'Id', sortable: true },
        { key: 'product', label: 'Item Name' },
        { key: 'pack', label: 'Pack' },
        { key: 'batch', label: 'Batch' },
        { key: 'rate', label: 'Rate' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'free_quantity', label: 'Free Quantity' },
        { key: 'percentage_discount', label: '% Dis.' },
        { key: 'volumatric_discount', label: 'Vol. Dis.' },
        { key: 'manufacturing', label: 'mfc' },
        { key: 'box_qty', label: 'Box Quantity' },
        { key: 'old_lot', label: 'Old Lot' },
        { key: 'lot', label: 'New Lot' },
      ],
    }
  },
  computed: {
    sortedItems() {
      // Use the items with color property that are already sorted
      return this.purchaseOrderDetail && this.purchaseOrderDetail.items
        ? this.purchaseOrderDetail.items
        : []
    },
    hasInvalidInputs() {
      if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items) {
        return true
      }

      // Check if any item has invalid inputs
      return this.purchaseOrderDetail.items.some(item => item.boxQtyInvalid || item.lotInvalid)
    },
  },
  created() {
    this.fetchDetails()
  },
  methods: {
    fillBoxQtyFromMfc() {
      if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items) return

      this.purchaseOrderDetail.items.forEach((item, index) => {
        if (item.manufacturing && !item.box_qty) {
          // Convert to integer if the float is a whole number (e.g., 10.0 => 10)
          const boxQty = parseFloat(item.manufacturing)
          this.$set(item, 'box_qty', Number.isInteger(boxQty) ? parseInt(boxQty, 10) : boxQty)
          this.updateItemColor(index)
        }
        // If there's already a box_qty value, leave it unchanged
      })
    },
    areLotValuesEqual(lot, oldLot) {
      // Check if value is empty
      const isEmpty = val => val === '' || val === 'nan' || val === null || Number.isNaN(val)

      // Handle case where both values are empty
      if (isEmpty(lot) && isEmpty(oldLot)) {
        return true
      }

      // If either value is not a string, fall back to exact comparison
      if (typeof lot !== 'string' || typeof oldLot !== 'string') {
        return lot === oldLot
      }

      // Normalize both strings by removing all spaces and converting to lowercase
      const normalizedLot = lot.replace(/\s+/g, '').toLowerCase()
      const normalizedOldLot = oldLot.replace(/\s+/g, '').toLowerCase()

      return normalizedLot === normalizedOldLot
    },
    fillLotFromOldLot(itemIndex) {
      if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items) return

      // Confirm before proceeding
      // if (!confirm('Fill all New Lot values from Old Lot? This will overwrite your current entries.')) return
      if (typeof itemIndex === 'number' && itemIndex !== undefined) {
        const item = this.purchaseOrderDetail.items[itemIndex]
        if (item && item.old_lot) {
          this.$set(item, 'lot', item.old_lot)
          this.updateItemColor(itemIndex)
        }
        return
      }
      // Update all lot values with old_lot values
      this.purchaseOrderDetail.items.forEach((item, index) => {
        if (item.old_lot && !item.lot) {
          this.$set(item, 'lot', item.old_lot)
          this.updateItemColor(index)
        }
      })
    },
    resetSingleLot(itemIndex) {
      if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items) return
      // if (!confirm('Fill all New Lot values from Old Lot? This will overwrite your current entries.')) return
      if (typeof itemIndex === 'number' && itemIndex !== undefined) {
        const item = this.purchaseOrderDetail.items[itemIndex]
        if (item && item.lot) {
          this.$set(item, 'lot', '')
          this.updateItemColor(itemIndex)
        }
      }
    },
    resetLot() {
      if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items) return
      // Update all lot values with old_lot values
      this.purchaseOrderDetail.items.forEach((item, index) => {
        this.$set(item, 'lot', '')
        this.updateItemColor(index)
      })
    },
    fetchDetails() {
      axios.get(`/invoices/received_purchase_orders?invoice_id=${this.invoiceId}`)
        .then(res => {
          // Direct assignment without destructuring
          const [firstItem] = res.data

          // Apply color logic and sorting, and add validation flags
          const updatedItems = firstItem.items
            .map(item => {
              // Add validation status flags (but don't show them yet)
              const boxQtyInvalid = this.isBoxQtyInvalid(item.box_qty)
              const lotInvalid = this.isLotInvalid(item.lot)

              // Get the color
              const coloredItem = this.assignItemColor({
                ...item,
                boxQtyInvalid,
                lotInvalid,
              })

              return coloredItem
            })
            .sort((a, b) => {
              const colorOrder = { red: 0, green: 1, white: 2 }
              return colorOrder[a.color] - colorOrder[b.color]
            })

          // Get first item from response
          this.purchaseOrderDetail = { ...firstItem, items: updatedItems } || {
            items: [],
          }

          // If it's not what we expected, handle different structures
          if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items) {
            // If we got an array directly, adapt to it
            if (Array.isArray(res.data)) {
              this.purchaseOrderDetail.items = res.data
            } else {
              // If it's a single object, wrap it
              this.purchaseOrderDetail.items = [res.data]
            }
          }

          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.detail || 'Error fetching details'
          this.loading = false
        })
    },
    // Check if box_qty is invalid (contains non-numeric characters)
    isBoxQtyInvalid(value) {
      if (value === undefined || value === null || value === '') {
        return false // Empty values are not invalid for validation
      }
      return typeof value === 'string' && /[^0-9.]/.test(value)
    },
    // Check if lot is invalid (contains characters other than numbers, +, and spaces)
    isLotInvalid(value) {
      if (value === undefined || value === null || value === '') {
        return false // Empty values are not invalid for validation
      }
      // Pattern: one or more digits, optionally followed by (space, +, space, one or more digits)
      return typeof value === 'string' && !/^\d+(?:\s*\+\s*\d+)?$/.test(value)
    },
    // Update an item and check validation
    updateItemColor(index) {
      if (!this.purchaseOrderDetail || !this.purchaseOrderDetail.items
          || index === undefined || index < 0
          || index >= this.purchaseOrderDetail.items.length) {
        return
      }

      // Get the current item from the array
      const currentItem = this.purchaseOrderDetail.items[index]

      // Check for validation errors
      const boxQtyInvalid = this.isBoxQtyInvalid(currentItem.box_qty)
      const lotInvalid = this.isLotInvalid(currentItem.lot)

      // Create a new item with updated color and validation status
      const updatedItem = this.assignItemColor({
        ...currentItem,
        boxQtyInvalid,
        lotInvalid,
      })

      // Replace the item in the array with the updated one
      this.$set(this.purchaseOrderDetail.items, index, updatedItem)
    },
    // Helper method to assign color to an item based on conditions
    assignItemColor(item) {
      const { manufacturing } = item
      const boxQty = item.box_qty
      const { lot } = item
      const oldLot = item.old_lot
      const boxQtyInvalid = item.boxQtyInvalid || this.isBoxQtyInvalid(boxQty)
      const lotInvalid = item.lotInvalid || this.isLotInvalid(lot)

      // Check if value is empty
      const isEmpty = val => val === '' || val === 'nan' || val === null || Number.isNaN(val)

      // Improved check for equality handling numeric strings
      const areEqual = (val1, val2) => {
        // Handle case where both values are empty
        if (isEmpty(val1) && isEmpty(val2)) {
          return true
        }

        // For manufacturing and box_qty comparison (numeric comparison)
        if ((val1 === manufacturing && val2 === boxQty) || (val2 === manufacturing && val1 === boxQty)) {
          // Try to convert both values to numbers
          const isNumeric1 = !Number.isNaN(parseFloat(val1))
          const isNumeric2 = !Number.isNaN(parseFloat(val2))

          // If both are numeric, compare their float values
          if (isNumeric1 && isNumeric2) {
            // Parse to ensure '10.0' equals 10
            return parseFloat(val1) === parseFloat(val2)
          }
        }

        // For lot and oldLot comparison with special handling for expressions like "5+2"
        if ((val1 === lot && val2 === oldLot) || (val2 === lot && val1 === oldLot)) {
          // If either value is not a string, fall back to exact comparison
          if (typeof val1 !== 'string' || typeof val2 !== 'string') {
            return val1 === val2
          }

          // Normalize both strings by removing all spaces and converting to lowercase
          const normalizedVal1 = val1.replace(/\s+/g, '').toLowerCase()
          const normalizedVal2 = val2.replace(/\s+/g, '').toLowerCase()

          return normalizedVal1 === normalizedVal2
        }

        // Default to exact comparison for all other cases
        return val1 === val2
      }

      let color

      // Invalid inputs take precedence
      if (boxQtyInvalid || lotInvalid) {
        color = 'red'
      } else if (isEmpty(manufacturing) && isEmpty(boxQty) && isEmpty(lot) && isEmpty(oldLot)) {
        color = 'white'
      } else if (areEqual(manufacturing, boxQty) && areEqual(lot, oldLot)) {
        color = 'green'
      } else {
        color = 'red'
      }

      return {
        ...item,
        color,
        boxQtyInvalid,
        lotInvalid,
      }
    },
    rowClass(item, type) {
      if (type !== 'row' || !item) return ''

      // Use the color property to determine the class
      if (item.color === 'red') {
        return 'table-danger text-white'
      }
      if (item.color === 'green') {
        return 'table-success text-white'
      }

      // Default (white)
      return ''
    },
    // Validate all inputs and then proceed with save/update
    validateAndSave(action) {
      this.purchaseOrderDetail.items.forEach((item, index) => {
        this.updateItemColor(index)
        // Modify via index instead of the parameter
        if (this.purchaseOrderDetail.items[index].lot && typeof this.purchaseOrderDetail.items[index].lot === 'string') {
          this.purchaseOrderDetail.items[index].lot = this.purchaseOrderDetail.items[index].lot
            .replace(/\s/g, '') // Remove all spaces
            .replace(/\+/g, ' + ') // Add spaces around each +
        }
      })

      // Turn on validation mode to show errors
      this.validateMode = true

      // If there are validation errors, stop here
      if (this.hasInvalidInputs) {
        // this.loadingError = 'Please correct highlighted fields before submitting'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please correct highlighted fields before submitting',
            icon: 'CrossIcon',
            variant: 'danger',
          },
        })
        return
      }

      // Clear any previous error
      this.loadingError = null

      // Proceed with save or update
      if (action === 'approve') {
        this.save()
      } else {
        this.update()
      }
    },
    save() {
      this.saving = true
      axios.put(`/invoices/recived_purchase_order/${this.purchaseOrderDetail.id}/approve/`, { items: this.purchaseOrderDetail.items })
        .then(res => {
          // For toast issue, using alert instead
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.detail || res?.data?.success || 'Purchase Order Approved',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.saving = false
          this.$emit('refresh')
          this.modalShow = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.detail || 'Error Approving Purchase Order'
          this.saving = false
        })
    },
    update() {
      this.saving = true
      axios.put(`/invoices/recived_purchase_order/${this.purchaseOrderDetail.id}/edit/`, { items: this.purchaseOrderDetail.items })
        .then(res => {
          // For toast issue, using alert instead
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.detail || res?.data?.success || 'Purchase Order Updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.saving = false
          this.$emit('refresh')
          this.modalShow = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.detail || 'Error Updating Purchase Order'
          this.saving = false
        })
    },
    formateNumber(value) {
      if (value === undefined || value === null) return ''
      try {
        return Number(value).toFixed(2)
      } catch (e) {
        return value
      }
    },
    aknowledgeHandler() {
      this.purchaseOrderDetail.no_match_aknowleged = true
    },
  },
}
</script>

<style scoped>
.purchase-order-modal ::v-deep .modal-body {
  overflow: hidden !important;
  padding: 0 !important;
  max-height: 80vh !important;
  display: flex !important;
  flex-direction: column !important;
}

.table-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
}

.compact-table {
  width: 100%;
}

.compact-table ::v-deep td {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.compact-table ::v-deep thead {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
  background-color: #f8f9fa !important;
}

.border-table ::v-deep thead th {
  border: 1px solid #dee2e6;
  background-color: #f8f9fa;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

.border-table ::v-deep tbody td {
  border: 1px solid #dee2e6;
}

.modal-footer {
  padding: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
